import { Task } from '@lit/task';
import { LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { InsightsService } from '../../../cross-framework-lib/insights-service/insights-service';
import { PageMeta } from '../../../cross-framework-lib/insights-service/insights.models';
import { importCrossFrameworkLib } from '../../util/import-cross-framework-lib';

/**
 * Time tracking component that allows users to track time spent on activities.
 */
@customElement('lua-time-tracking')
export class LuaTimeTracking extends LitElement {
  private insightsService: InsightsService;
  private intervalId: number | null = null;
  private readonly HEARTBEAT_INTERVAL_MS = 5000;

  @property({ type: Number }) courseId: number;
  @property({ type: Number }) moduleId: number;
  @property({ type: String }) pagegroup = '';
  @property({ type: String }) pagename = '';

  private heartBeatTask = new Task(this, {
    task: async () => {
      const { InsightsService } = await importCrossFrameworkLib();
      this.insightsService = InsightsService.getInstance();

      if (!this.insightsService) {
        this.insightsService.init({
          trackingServiceUrl: '/data-insight/events',
          enabledLogging: true,
        });
      }

      this.startHeartbeat();
      this.setupEventListeners();
    },
    args: (): [] => [],
  });

  private sendHeartbeat = (): void => {
    this.insightsService.heartBeatEvent({
      ...this.getPageMeta(),
      course_id: this.courseId,
      module_id: this.moduleId,
      span_ms: this.HEARTBEAT_INTERVAL_MS,
    });
  };

  private startHeartbeat = (): void => {
    if (this.intervalId === null) {
      this.sendHeartbeat();
      this.intervalId = window.setInterval(this.sendHeartbeat, this.HEARTBEAT_INTERVAL_MS);
    }
  };

  private stopHeartbeat = (): void => {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };

  private handleVisibilityChange = (): void => {
    document.visibilityState === 'visible' ? this.startHeartbeat() : this.stopHeartbeat();
  };

  private setupEventListeners = (): void => {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.stopHeartbeat);
    window.addEventListener('blur', this.stopHeartbeat);
    window.addEventListener('focus', this.startHeartbeat);
  };

  private removeEventListeners = (): void => {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.stopHeartbeat);
    window.removeEventListener('blur', this.stopHeartbeat);
    window.removeEventListener('focus', this.startHeartbeat);
  };

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopHeartbeat();
    this.removeEventListeners();
  }

  private getPageMeta(): PageMeta {
    return {
      pagegroup: this.pagegroup,
      pagename: this.pagename,
    };
  }

  override render() {
    return this.heartBeatTask.render({
      pending: () => '',
      complete: () => '',
      error: () => '',
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-time-tracking': LuaTimeTracking;
  }
}
